// DEPRECATED: Prefer using the useInitiatives() hook from app/src/hooks/useInitiatives.ts directly.
// This context is kept for backward compatibility.
//
// Tree-aware org-unit helpers are co-located with this context so
// initiative-side consumers (filter dropdowns, cascade filters) have a
// single import location while UseCaseContext is still the public
// entry point. The actual implementations live in
// ``@/hooks/useOrgUnits`` so the tree shape is also reachable from
// pages that don't depend on the initiative context.

import React, { createContext, useContext, ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { initiativeService } from "@/services/initiativeService";
import {
  flattenOrgUnitTree,
  isInOrgUnitSubtree,
  useFlatOrgUnitList,
  type FlatOrgUnitEntry,
} from "@/hooks/useOrgUnits";

// Re-export tree-aware org-unit helpers so callers can import them
// alongside ``useUseCases`` without having to know the internal hook
// path. These are pure pass-throughs — see ``@/hooks/useOrgUnits``
// for the implementations and JSDoc.
export {
  flattenOrgUnitTree,
  isInOrgUnitSubtree,
  useFlatOrgUnitList,
  type FlatOrgUnitEntry,
};

export interface OrgUnitApproval {
  // ADR-0014: org_unit id (not a name). Resolve display name via
  // UseCaseData.orgUnitNames.
  org_unit: string;
  status: "pending" | "approved" | "tentative" | "rejected";
  approvedBy?: string;
  approvedAt?: string;
  comment?: string;
}

export interface RACIEntry {
  task: string;
  personName: string;
  personType: "team" | "vendor";
  role: "R" | "A" | "C" | "I" | "";
}

export interface VendorBid {
  vendorName: string;
  status: "invited" | "submitted" | "accepted" | "rejected";
  proposedValue?: string;
  submittedAt?: string;
  notes?: string;
}

export interface VendorBriefing {
  title: string;
  requirements: string;
  deadline: string;
  budget: string;
  createdAt: string;
}

export type UserCoverage = "low" | "medium" | "high";
export type WorkflowDependency = "optional" | "recommended" | "required" | "system-embedded";
export type AdoptionDirection = "increasing" | "stable" | "declining";
export type AdoptionState = "strong" | "weak" | "at-risk";

export interface InitiativeBusinessValueSummary {
  state: "missing" | "draft" | "needs_confirmation" | "complete" | "review_overdue";
  sourceStatus?: "draft" | "confirmed";
  expectedOutcome?: string;
  kpiName?: string;
  baselineValue?: number;
  targetValue?: number;
  unit?: string;
  targetDate?: string;
  expectedFinancialValue?: number;
  forecastCost?: number;
  currency?: string;
  benefitOwnerUserId?: string;
  confidence?: "low" | "medium" | "high";
  nextReviewDate?: string;
  latestActualValue?: number;
  latestMeasurementDate?: string;
  progress?: number;
  reviewOverdue: boolean;
  changeReviewRequired: boolean;
  missingFields: string[];
  materialChanges: string[];
}

export interface AdoptionConfirmation {
  activeUse: "yes" | "partial" | "no";
  userCoverage: UserCoverage;
  usedLast30Days: boolean;
  workflowDependency: WorkflowDependency;
  keyPersonDependency: boolean;
  confirmedAt: string;
  confirmedBy: string;
}

export interface AdoptionEvidence {
  id: string;
  type: "sample-output" | "screenshot" | "sop-update" | "manager-confirmation";
  description: string;
  attachmentUrl?: string;
  addedAt: string;
  addedBy: string;
}

export interface RiskItem {
  text: string;
  status: "open" | "closed";
}

export interface InitiativeComment {
  id: string;
  type?: "comment" | "system";
  action?: string;
  author: string;
  text: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

export type ActivityLogEntry = InitiativeComment;

export interface UseCaseData {
  id: string;
  name: string;
  // ADR-0014: org_unit / org_units carry org_unit IDs (not names);
  // orgUnitApprovals records are keyed by org_unit id. Display names are
  // resolved from orgUnitNames {id: name}.
  org_unit: string;
  org_units?: string[];
  orgUnitApprovals?: OrgUnitApproval[];
  orgUnitNames?: Record<string, string>;
  nominationStatus?: "draft" | "pending" | "approved" | "rejected";
  stage: "idea" | "pilot" | "live" | "optimised";
  impact: "high" | "medium" | "low";
  risk: "high" | "medium" | "low";
  readiness: number;
  owner: string;
  ownerUserId?: string;
  description: string;
  team: string[];
  readers?: string[];
  vendors?: string[];
  raci?: RACIEntry[];
  budget: string;
  startDate: string;
  endDate: string;
  ragRating: "not_assessed" | "red" | "amber" | "green";
  risks: RiskItem[];
  initiativeType?: "use-case" | "governance" | "training";
  archived?: boolean;
  vendorBriefing?: VendorBriefing;
  vendorBids?: VendorBid[];
  adoptionConfirmation?: AdoptionConfirmation;
  adoptionEvidence?: AdoptionEvidence[];
  adoptionDirection?: AdoptionDirection;
  businessValue?: InitiativeBusinessValueSummary;
  comments?: InitiativeComment[];
  finalApproval?: OrgUnitApproval;
  pendingChanges?: boolean;
  lastChangeBy?: string;
  lastChangeAt?: string;
}

export type InitiativeCreateData = Omit<UseCaseData, "id"> & {
  teamUserIds?: string[];
  readerUserIds?: string[];
};

interface UseCaseContextType {
  useCases: UseCaseData[];
  isLoading: boolean;
  addUseCase: (useCase: InitiativeCreateData) => Promise<UseCaseData>;
  getUseCase: (id: string) => UseCaseData | undefined;
  updateUseCase: (id: string, updates: Partial<UseCaseData>) => void;
  updateUseCaseAsync: (id: string, updates: Partial<UseCaseData>) => Promise<UseCaseData>;
  archiveUseCase: (id: string) => void;
  restoreUseCase: (id: string) => void;
}

const UseCaseContext = createContext<UseCaseContextType | undefined>(undefined);

export function UseCaseProvider({ children }: { children: ReactNode }) {
  const { hasTenant } = useAuth() as { hasTenant: boolean };
  const queryClient = useQueryClient();

  const { data: useCases = [], isLoading } = useQuery({
    queryKey: ["initiatives"],
    queryFn: async () => {
      const [active, archived] = await Promise.all([
        initiativeService.list(false),
        initiativeService.list(true),
      ]);
      return [...active, ...archived];
    },
    enabled: hasTenant,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["initiatives"] });

  const addMutation = useMutation({
    mutationFn: (data: InitiativeCreateData) => initiativeService.create(data),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<UseCaseData> }) =>
      initiativeService.update(id, data),
    onSuccess: invalidate,
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => initiativeService.archive(id),
    onSuccess: invalidate,
  });

  const addUseCase = async (useCase: InitiativeCreateData): Promise<UseCaseData> => {
    return addMutation.mutateAsync({ ...useCase, initiativeType: useCase.initiativeType ?? "use-case", archived: false });
  };

  const getUseCase = (id: string) => useCases.find((uc) => uc.id === id);

  const updateUseCase = (id: string, updates: Partial<UseCaseData>) => {
    updateMutation.mutate({ id, data: updates });
  };

  const updateUseCaseAsync = (id: string, updates: Partial<UseCaseData>) =>
    updateMutation.mutateAsync({ id, data: updates });

  const archiveUseCase = (id: string) => archiveMutation.mutate(id);

  // restoreUseCase also calls archive endpoint which toggles the flag
  const restoreUseCase = (id: string) => archiveMutation.mutate(id);

  return (
    <UseCaseContext.Provider
      value={{ useCases, isLoading, addUseCase, getUseCase, updateUseCase, updateUseCaseAsync, archiveUseCase, restoreUseCase }}
    >
      {children}
    </UseCaseContext.Provider>
  );
}

export function useUseCases() {
  const context = useContext(UseCaseContext);
  if (!context) {
    throw new Error("useUseCases must be used within a UseCaseProvider");
  }
  return context;
}
